import { useEffect, useRef } from "react";
import {
  Bold,
  Code2,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Underline,
} from "lucide-react";

const escapeHtml = (value) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

export const getPlainTextFromHtml = (html) =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|pre|ul|ol)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isSameHtml = (left, right) =>
  (left || "").replace(/\s+/g, " ").trim() === (right || "").replace(/\s+/g, " ").trim();

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 180,
}) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;
    if (isSameHtml(editorRef.current.innerHTML, value)) return;
    editorRef.current.innerHTML = value || "";
  }, [value]);

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const applyCommand = (command, commandValue = null) => {
    focusEditor();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    const safeHtml = escapeHtml(text).replace(/\n/g, "<br>");
    focusEditor();
    document.execCommand("insertHTML", false, safeHtml);
    onChange(editorRef.current?.innerHTML || "");
  };

  const handleCodeBlock = () => {
    focusEditor();
    document.execCommand("formatBlock", false, "pre");
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div className="support-rich-editor">
      <div className="support-rich-toolbar" role="toolbar" aria-label="Mise en forme du texte">
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("bold")}>
          <Bold className="w-4 h-4" />
          Gras
        </button>
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("italic")}>
          <Italic className="w-4 h-4" />
          Italique
        </button>
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("underline")}>
          <Underline className="w-4 h-4" />
          Souligne
        </button>
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("insertUnorderedList")}>
          <List className="w-4 h-4" />
          Liste
        </button>
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("insertOrderedList")}>
          <ListOrdered className="w-4 h-4" />
          Liste num.
        </button>
        <button type="button" className="support-rich-btn" onClick={handleCodeBlock}>
          <Code2 className="w-4 h-4" />
          Code
        </button>
        <button type="button" className="support-rich-btn" onClick={() => applyCommand("formatBlock", "p")}>
          <Pilcrow className="w-4 h-4" />
          Texte
        </button>
      </div>

      <div
        ref={editorRef}
        className="support-rich-input"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        style={{ minHeight }}
      />
    </div>
  );
}
